import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
} from '@mui/material';
import { useCommonTranslation } from 'c-translation';
import type { DashboardTableColumn } from './dashboardTableData';
import { sortTableRows } from './dashboardTableData';

type Props = {
    columns: DashboardTableColumn[];
    rows: Record<string, number | string | null>[];
    // Closes the table; already worked out (a ratio is recomputed on the totals, not summed).
    totals?: Record<string, number | string | null> | null;
    pageSize: number;
    pageSizeOptions?: number[];
    // Fill the space it's given, scrolling the body under a sticky header.
    fill?: boolean;
    // Rows it returns true for open something when clicked, e.g. their campaign.
    canOpenRow?: (row: Record<string, number | string | null>) => boolean;
    onOpenRow?: (row: Record<string, number | string | null>) => void;
    openRowLabel?: string;
};

// Every row of a widget's result, a page at a time, sortable by any column.
const DashboardDataTable: React.FC<Props> = ({
    columns,
    rows,
    totals,
    pageSize: initialPageSize,
    pageSizeOptions = [10, 25, 50, 100],
    fill = false,
    canOpenRow,
    onOpenRow,
    openRowLabel,
}) => {
    const t = useCommonTranslation();
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(initialPageSize);
    // No column chosen keeps the widget's own ranking.
    const [sort, setSort] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    useEffect(() => setPageSize(initialPageSize), [initialPageSize]);
    // A different result (a filter changed, a new page size) starts again at the top.
    useEffect(() => setPage(0), [rows, pageSize]);

    const sorted = useMemo(
        () => (sort ? sortTableRows(rows, columns, sort.key, sort.direction) : rows),
        [rows, columns, sort],
    );
    const visible = sorted.slice(page * pageSize, page * pageSize + pageSize);
    const options = [...new Set([...pageSizeOptions, initialPageSize])].sort((a, b) => a - b);

    const toggleSort = (key: string) =>
        setSort(current => {
            if (current?.key !== key) {
                const column = columns.find(c => c.key === key);
                return { key, direction: column?.numeric ? 'desc' : 'asc' };
            }
            return current.direction ===
                (columns.find(c => c.key === key)?.numeric ? 'desc' : 'asc')
                ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
                : null;
        });

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                height: fill ? '100%' : undefined,
                minHeight: 0,
            }}
        >
            <TableContainer sx={{ flex: fill ? 1 : undefined, minHeight: 0 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map(col => (
                                <TableCell
                                    key={col.key}
                                    align={col.numeric ? 'right' : 'left'}
                                    sortDirection={sort?.key === col.key ? sort.direction : false}
                                    sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                                >
                                    <TableSortLabel
                                        active={sort?.key === col.key}
                                        direction={sort?.key === col.key ? sort.direction : 'asc'}
                                        onClick={() => toggleSort(col.key)}
                                    >
                                        {col.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {visible.map((row, index) => {
                            const opens = onOpenRow != null && (canOpenRow?.(row) ?? true);
                            return (
                                <TableRow
                                    // eslint-disable-next-line react/no-array-index-key -- rows have no identity of their own; the key only has to be stable within a page
                                    key={index}
                                    hover
                                    {...(opens
                                        ? {
                                              onClick: () => onOpenRow(row),
                                              onKeyDown: (e: React.KeyboardEvent) => {
                                                  if (e.key !== 'Enter') return;
                                                  e.preventDefault();
                                                  onOpenRow(row);
                                              },
                                              tabIndex: 0,
                                              title: openRowLabel,
                                              sx: { cursor: 'pointer' },
                                          }
                                        : {})}
                                >
                                    {columns.map(col => (
                                        <TableCell
                                            key={col.key}
                                            align={col.numeric ? 'right' : 'left'}
                                            sx={{ whiteSpace: col.numeric ? 'nowrap' : undefined }}
                                        >
                                            {col.format(row[col.key])}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                    </TableBody>
                    {totals && (
                        <TableFooter>
                            <TableRow>
                                {columns.map((col, i) => (
                                    <TableCell
                                        key={col.key}
                                        align={col.numeric ? 'right' : 'left'}
                                        sx={{
                                            fontWeight: 700,
                                            color: 'text.primary',
                                            position: 'sticky',
                                            bottom: 0,
                                            bgcolor: 'background.paper',
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {i === 0 && !col.numeric
                                            ? t('Pages.DashboardHome.custom.total')
                                            : col.numeric
                                            ? col.format(totals[col.key])
                                            : ''}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableFooter>
                    )}
                </Table>
            </TableContainer>
            {rows.length > Math.min(...options) && (
                <TablePagination
                    component="div"
                    count={rows.length}
                    page={Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1))}
                    rowsPerPage={pageSize}
                    rowsPerPageOptions={options}
                    onPageChange={(_, next) => setPage(next)}
                    onRowsPerPageChange={e => setPageSize(Number(e.target.value))}
                    labelRowsPerPage={t('Pages.DashboardHome.custom.rowsPerPage')}
                    labelDisplayedRows={({ from, to, count }) =>
                        t('Pages.DashboardHome.custom.displayedRows', { from, to, count })
                    }
                    sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 40 } }}
                />
            )}
        </Box>
    );
};

export default DashboardDataTable;
